import { useCallback, useState } from "react";
import { useOrderingMessage } from "../../useOrderingMessage";
import { useTranslation } from "react-i18next";
import { TransferWaiterModal } from "./TransferWaiterModal";
import { ConfirmActionModal } from "../ConfirmActionModal";
import {
  useBills,
  useTransferBillWaiter,
  useTransferAllBillsWaiter,
} from "../../../../../hooks/useBills";
import { useAppSelector } from "../../../../../store/hooks";
import { useIsEdgeLocal } from "../../../../../hooks/useEdgeMode";
import { getApiErrorMessage } from "../../../../../utils/translations";

interface TransferBillFlowProps {
  /** Модалът за избор на сервитьор („Опции" → „Смяна на сервитьор"). */
  open: boolean;
  onClose: () => void;
  billId: number | null;
  tableName: string;
  currentWaiterId: number | null;
  totalAmount: string;
}

// „Смяна на сервитьор" от футъра на масите: модалът за избор + двата въпроса
// за дублирано име (една сметка / всичките ми маси). Изваден от TableFooter.
export const TransferBillFlow = ({
  open,
  onClose,
  billId,
  tableName,
  currentWaiterId,
  totalAmount,
}: TransferBillFlowProps) => {
  const { t } = useTranslation();
  const message = useOrderingMessage();
  const { data: bills = [] } = useBills();
  const userId = useAppSelector((state) => state.auth.userId);
  const transferBillWaiterMutation = useTransferBillWaiter();
  const transferAllBillsWaiterMutation = useTransferAllBillsWaiter();
  // Локален режим (docs/edge.md): прехвърлянето на ЕДНА сметка касата
  // сервира (`PATCH /bills/{id}/waiter`), „всичките ми маси" (transfer-all)
  // остава извън обхвата — бутонът стои, disabled с подсказка.
  const edgeLocal = useIsEdgeLocal();

  // Сервитьорът, към когото прехвърляме, докато чакаме потвърждение за
  // дублирано име (null = няма висящ въпрос).
  const [duplicateNameWaiterId, setDuplicateNameWaiterId] = useState<
    number | null
  >(null);
  // Същото за „Прехвърли всичките ми маси".
  const [duplicateNameTransferAllWaiterId, setDuplicateNameTransferAllWaiterId] =
    useState<number | null>(null);

  // Моите отворени маси — за „Прехвърли всичките ми N маси" в модала.
  const myOpenTablesCount = bills.filter(
    (bill) => bill.status === "open" && bill.waiter_id === userId,
  ).length;

  const handleTransferWaiterSubmit = useCallback(
    async (waiterId: number | null, allowDuplicateName = false) => {
      if (!billId || waiterId == null) return;
      try {
        await transferBillWaiterMutation.mutateAsync({
          billId,
          waiterId,
          allowDuplicateName,
        });
        message.success(t("ordering.transferBillSuccess"));
        onClose();
        setDuplicateNameWaiterId(null);
      } catch (err: unknown) {
        const response = (
          err as { response?: { status?: number; data?: { detail?: string } } }
        )?.response;
        // 409 = получателят вече има отворена маса със същото име. Не е грешка,
        // а въпрос: питаме и при потвърждение пращаме същото с allowDuplicateName.
        if (response?.status === 409 && !allowDuplicateName) {
          onClose();
          setDuplicateNameWaiterId(waiterId);
          return;
        }
        message.error(getApiErrorMessage(err, t("ordering.transferBillError")));
      }
    },
    [billId, transferBillWaiterMutation, message, t, onClose],
  );

  // „Прехвърли всичките ми маси" — същият 409-договор: питаме за дублираните
  // имена и повтаряме с allowDuplicateName.
  const handleTransferAllSubmit = useCallback(
    async (waiterId: number, allowDuplicateName = false) => {
      try {
        const result = await transferAllBillsWaiterMutation.mutateAsync({
          waiterId,
          allowDuplicateName,
        });
        message.success(
          t("ordering.transferAllBillsSuccess", { count: result.transferred }),
        );
        onClose();
        setDuplicateNameTransferAllWaiterId(null);
      } catch (err: unknown) {
        const response = (err as { response?: { status?: number } })?.response;
        if (response?.status === 409 && !allowDuplicateName) {
          onClose();
          setDuplicateNameTransferAllWaiterId(waiterId);
          return;
        }
        message.error(
          getApiErrorMessage(err, t("ordering.transferAllBillsError")),
        );
      }
    },
    [transferAllBillsWaiterMutation, message, t, onClose],
  );

  return (
    <>
      <TransferWaiterModal
        open={open}
        onBack={onClose}
        onSubmit={handleTransferWaiterSubmit}
        currentWaiterId={currentWaiterId}
        submitting={
          transferBillWaiterMutation.isPending ||
          transferAllBillsWaiterMutation.isPending
        }
        title={t("ordering.transferBillTitle")}
        confirmLabel={t("ordering.transferBillConfirm")}
        userRole="ordering"
        transferAmount={parseFloat(totalAmount)}
        onTransferAll={(waiterId) => void handleTransferAllSubmit(waiterId)}
        myOpenTablesCount={myOpenTablesCount}
        transferAllLockedTooltip={
          edgeLocal ? t("edge.unavailableOffline") : undefined
        }
      />
      <ConfirmActionModal
        open={duplicateNameWaiterId !== null}
        title={t("duplicateTableName.title")}
        message={t("duplicateTableName.transferMessage", { name: tableName })}
        question={t("duplicateTableName.question", { name: tableName })}
        confirmLabel={t("duplicateTableName.confirm")}
        onCancel={() => setDuplicateNameWaiterId(null)}
        onConfirm={() => {
          const waiterId = duplicateNameWaiterId;
          setDuplicateNameWaiterId(null);
          void handleTransferWaiterSubmit(waiterId, true);
        }}
      />
      <ConfirmActionModal
        open={duplicateNameTransferAllWaiterId !== null}
        title={t("duplicateTableName.title")}
        message={t("duplicateTableName.transferAllMessage")}
        question={t("duplicateTableName.transferAllQuestion")}
        confirmLabel={t("duplicateTableName.confirm")}
        onCancel={() => setDuplicateNameTransferAllWaiterId(null)}
        onConfirm={() => {
          const waiterId = duplicateNameTransferAllWaiterId;
          setDuplicateNameTransferAllWaiterId(null);
          if (waiterId !== null) void handleTransferAllSubmit(waiterId, true);
        }}
      />
    </>
  );
};
